import { defineComponent as _defineComponent } from 'vue'
import { useMyProjects } from '@/views/my-projects/composables/useMyProjects';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import {
  computed, PropType, ref, toRefs, watch
} from 'vue';
import toggleArrayItem from '@/utils/toggleArrayItem';
import { ProjectOffer, ProjectOfferWithProjectReference, ProjectWithOffers } from '@/models/MyProjects';
import { useArrayMap } from '@vueuse/core';
import useI18n from '@/mixins/useI18n';
import { getNameSortString, getProjectCreatorName } from '@/utils/user';
import BCXTable from '@/components/BCXTable.vue';
import BCXUserBannerSimple from '@/components/molecules/BCXUserBannerSimple.vue';
import MyProjectsOffersStatusSelector from '@/views/my-projects/components/offers/MyProjectsOffersStatusSelector.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import { BcxTableFilter } from '@/models/BcxTable';
import { BcxTablePinnedItemPosition } from '@/mixins/useBcxTableSearchFilterSort';
import animationFrameWait from '@/utils/animationFrameWait';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsOffersTable',
  props: {
  offers: {
    type: Array as PropType<(ProjectOffer & ProjectOfferWithProjectReference)[]>,
    default: () => []
  },
  type: {
    type: String as PropType<'made'|'received'>,
    default: 'default'
  }
},
  setup(__props) {

const props = __props;



const { t } = useI18n();

const { offers } = toRefs(props);

const { isNarrow } = useMyProjects();
const { getFormattedTimeByAge, getFormattedDate } = useFormattedDateTime();

const offerMap:Partial<Record<ProjectOffer['status'], ProjectOffer['status']>> = {
  AUTO_REJECTED: 'DECLINED',
  WITHDRAWN_QUEUED: 'WITHDRAWN',
  OPEN_CHANGED: 'OPEN',
};

const mappedOffers = useArrayMap(offers, (offer) => {
  let { status } = offer;
  status = offerMap[status] ?? status;
  return ({ ...offer, status });
});

const selectedOfferIds = ref<string[]>([]);

const visibleRows = ref<(ProjectOffer & ProjectOfferWithProjectReference)[]>([]);

const pinnedItems = ref<BcxTablePinnedItemPosition[]>([]);

const setPinnedItemsToSelectedOffers = () => {
  pinnedItems.value = selectedOfferIds.value.map((id) => {
    const position = visibleRows.value.findIndex((row) => row.id === id);
    return ({
      idValue: id,
      idField: 'id',
      position
    });
  });
};

const formatDate = (timestamp: string, dateformat: 'short'|'diff' = 'diff') => (dateformat === 'diff' ? getFormattedTimeByAge(timestamp) : getFormattedDate(timestamp));

const resetPinnedItems = async () => {
  pinnedItems.value = [];
  await animationFrameWait(2);
  setPinnedItemsToSelectedOffers();
};

watch(() => selectedOfferIds.value, () => {
  setPinnedItemsToSelectedOffers();
});

const mobileDetails = ref<HTMLElement[] | null>(null);

const selectOffer = (id: string, event?:PointerEvent) => {
  const eventTarget = event?.target as Node;
  if (eventTarget) {
    const isInDetails = mobileDetails.value?.some?.((elm) => elm.contains(eventTarget));
    if (isInDetails) return;
  }
  const ids = [...selectedOfferIds.value];
  toggleArrayItem(ids, id);
  selectedOfferIds.value = ids;
};

const isSelectedOffer = (id:string) => selectedOfferIds.value.includes(id);

const isOfferRead = (lastUpdated: string, read?: string) => {
  if (!read) return false;

  const readDate = new Date(read);
  return readDate > new Date(lastUpdated);
};

const fieldNames = computed(() => [
  props.type === 'received' ? 'from' : 'project',
  'valid', 'date', 'status',
]);

const headerTitles = computed(() => [
  props.type === 'received' ? t('my-projects.offers.columns.from') : t('my-projects.offers.columns.project'),
  t('my-projects.offers.columns.valid'),
  t('my-projects.offers.columns.date'),
  t('my-projects.offers.columns.status'),
]);

const mobileGridAreas = computed(() => (props.type === 'received'
  ? 'from from \n status . \n date valid' : 'project project \n status . \n date valid'));

const offerSortFieldValue = (fieldName: string, obj: ProjectOffer & ProjectWithOffers): string | number => {
  switch (fieldName) {
    case 'project': return obj?.project?.title ?? '';
    case 'from': return getNameSortString(obj?.creator);
    case 'date': return obj.lastUpdated ?? '';
    case 'valid':
      if (obj.status !== 'OPEN' && obj.status !== 'NEGOTIATIONS') {
        return new Date('1901-01-01').getTime();
      }
      return obj.validUntil ?? '';

    case 'status': return obj.status ?? '';
  }
  return 0;
};

const offerDisplayFieldValue = (fieldName: string, obj: ProjectOffer & ProjectWithOffers): string => {
  switch (fieldName) {
    case 'project': return obj?.project?.title ?? '';
    case 'from': return obj.creator.userId;
    case 'date': return formatDate(obj.lastUpdated);
    case 'valid':
      if (obj.status === 'OPEN' || obj.status === 'NEGOTIATIONS' || obj.status === 'QUEUED') {
        return formatDate(obj.validUntil, 'short');
      }
      return '-';
    case 'status': return obj.status;
  }
  return '-';
};

const doesOfferExpireSoon = (validUntil: string): boolean => {
  if (!validUntil) return false;
  const diffDays = (new Date(validUntil).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000;
  return diffDays <= 1;
};

const allStates: ProjectOffer['status'][] = [
  'OPEN',
  'NEGOTIATIONS',
  'CONTRACTPROCESS',
  'ACCEPTED',
  'DECLINED',
  'WITHDRAWN',
  'EXPIRED',
  'QUEUED',
];

const availableStatusesInMappedData = computed(() => {
  const statusMap: Partial<Record<ProjectOffer['status'], boolean>> = Object.fromEntries(allStates.map((state) => [state, false]));
  mappedOffers.value.forEach((offer) => {
    statusMap[offer.status] = true;
  });
  if (props.type === 'received') statusMap.QUEUED = false;

  return Object.entries(statusMap)
    .filter(([, value]) => value)
    .map(([key]) => key) as ProjectOffer['status'][];
});

const tableFilter = computed(():BcxTableFilter => ({
  style: 'dropdown',
  visibleStates: availableStatusesInMappedData.value,
  states: allStates,
  labelCallback: (state: string) => `${t(`my-projects.offers.filter-values.${state}`)}`,
  stateCallback: (states: string[], { status }: ProjectOffer & ProjectWithOffers) => states.includes(status)
}));

const searchCallback = (searchTerm: string, obj: ProjectOffer & ProjectWithOffers) => {
  const searchBlock = (props.type === 'made' ? obj?.project?.title ?? '' : getProjectCreatorName(obj)).toLocaleLowerCase();
  return searchBlock.includes(searchTerm.toLocaleLowerCase());
};

const hasContent = computed(() => offers.value && offers.value.length > 0);


return { __sfc: true,props, t, offers, isNarrow, getFormattedTimeByAge, getFormattedDate, offerMap, mappedOffers, selectedOfferIds, visibleRows, pinnedItems, setPinnedItemsToSelectedOffers, formatDate, resetPinnedItems, mobileDetails, selectOffer, isSelectedOffer, isOfferRead, fieldNames, headerTitles, mobileGridAreas, offerSortFieldValue, offerDisplayFieldValue, doesOfferExpireSoon, allStates, availableStatusesInMappedData, tableFilter, searchCallback, hasContent, BCXTable, BCXUserBannerSimple, MyProjectsOffersStatusSelector, BCXMarkdown }
}

})